import React, { useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { motion } from 'framer-motion'
import { Check } from 'lucide-react'
import { Button } from '../components/intake/shared/Button'
import { useI18n } from '../i18n/I18nProvider'

export function CheckoutSuccess() {
  const { t } = useI18n()
  const [params] = useSearchParams()
  const sessionId = params.get('session_id')

  const [copied, setCopied] = useState(false)

  const shareUrl = useMemo(() => window.location.origin, [])
  const shareText = t('checkoutSuccess.shareText')

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl)
      setCopied(true)
      setTimeout(() => setCopied(false), 2500)
    } catch {
      // Fallback for older browsers
      const textarea = document.createElement('textarea')
      textarea.value = shareUrl
      document.body.appendChild(textarea)
      textarea.select()
      document.execCommand('copy')
      document.body.removeChild(textarea)
      setCopied(true)
      setTimeout(() => setCopied(false), 2500)
    }
  }

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'Niyyah',
          text: shareText,
          url: shareUrl,
        })
      } catch {
        // User cancelled or share failed — fall back to copy
        handleCopy()
      }
    } else {
      handleCopy()
    }
  }

  return (
    <div className="min-h-screen bg-[#FDFCFE] text-[#2D2A35] font-sans selection:bg-[#B5A8D4]/20 overflow-hidden">
      <div className="max-w-2xl mx-auto px-6 py-12 sm:py-20 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, ease: [0.25, 0.1, 0.25, 1] }}
          className="mb-16 flex justify-center"
        >
          <span className="font-semibold text-xl tracking-tight niyyah-gradient">
            Niyyah
          </span>
        </motion.div>

        <div className="flex flex-col items-center justify-center min-h-[70vh] text-center space-y-16 relative">
          {/* Success Icon */}
          <motion.div
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 1.2, ease: [0.25, 0.1, 0.25, 1] }}
            className="relative"
          >
            <div className="w-32 h-32 rounded-full animated-gradient-circle flex items-center justify-center organic-shadow">
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{
                  delay: 0.6,
                  duration: 0.8,
                  ease: [0.25, 0.1, 0.25, 1],
                }}
              >
                <Check className="w-16 h-16 text-white stroke-[1.5] relative z-10" />
              </motion.div>
            </div>
          </motion.div>

          {/* Title and Subtitle */}
          <div className="space-y-6 max-w-2xl mx-auto">
            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{
                delay: 0.4,
                duration: 1.2,
                ease: [0.25, 0.1, 0.25, 1],
              }}
              className="text-5xl sm:text-6xl font-normal text-[#2D2A35] tracking-tight"
            >
              {t('checkoutSuccess.title')}
            </motion.h1>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{
                delay: 0.6,
                duration: 1.2,
                ease: [0.25, 0.1, 0.25, 1],
              }}
              className="text-xl sm:text-2xl text-[#57534E] leading-relaxed font-light space-y-2"
            >
              <p>{t('checkoutSuccess.subtitleLine1')}</p>
              <p>{t('checkoutSuccess.subtitleLine2')}</p>
            </motion.div>
          </div>

          {/* Modern Summary Card */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{
              delay: 0.8,
              duration: 1.2,
              ease: [0.25, 0.1, 0.25, 1],
            }}
            className="w-full max-w-sm"
          >
            <div className="bg-white rounded-3xl p-8 border border-[#E8E4EE] shadow-lg overflow-hidden relative">
              <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-[#9B87C7] via-[#B5A8D4] to-[#9B87C7]" />

              <div className="space-y-6">
                {/* Status Section */}
                <div className="relative text-left">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-2 h-2 rounded-full bg-[#9B87C7] animate-pulse" />
                    <p className="text-xs text-[#9B95A3] font-medium uppercase tracking-widest">
                      {t('checkoutSuccess.statusLabel')}
                    </p>
                  </div>
                  <p className="text-2xl font-normal text-[#2D2A35] tracking-tight">
                    {t('checkoutSuccess.statusValue')}
                  </p>
                </div>

                {/* Divider */}
                <div className="relative h-px">
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-[#E8E4EE] to-transparent" />
                </div>

                {/* What's Next Section */}
                <div className="relative text-left">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-2 h-2 rounded-full bg-[#B5A8D4]" />
                    <p className="text-xs text-[#9B95A3] font-medium uppercase tracking-widest">
                      {t('checkoutSuccess.nextLabel')}
                    </p>
                  </div>
                  <p className="text-lg text-[#2D2A35] font-light leading-relaxed">
                    {t('checkoutSuccess.nextBody')}
                  </p>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Invite container */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{
              delay: 1.0,
              duration: 1.2,
              ease: [0.25, 0.1, 0.25, 1],
            }}
            className="w-full max-w-md"
          >
            <div
              className="relative border border-[#EDE8F4]/60 rounded-[2.5rem] px-8 py-10 overflow-hidden"
              style={{
                background:
                  'linear-gradient(135deg, rgba(155, 135, 199, 0.08) 0%, rgba(181, 168, 212, 0.12) 40%, rgba(155, 135, 199, 0.06) 100%)',
              }}
            >
              <div className="absolute -top-16 -right-16 w-40 h-40 rounded-full bg-[#B5A8D4]/10 blur-3xl pointer-events-none" />
              <div className="absolute -bottom-12 -left-12 w-32 h-32 rounded-full bg-[#9B87C7]/8 blur-2xl pointer-events-none" />

              <div className="relative z-10 space-y-6">
                <div className="space-y-3 text-center">
                  <h2 className="text-xl font-normal text-[#2D2A35] tracking-tight">
                    {t('checkoutSuccess.inviteTitle')}
                  </h2>
                  <p className="text-sm text-[#6B6575] font-light leading-relaxed">
                    {t('checkoutSuccess.inviteBody')}
                  </p>
                </div>

                <div className="flex items-start justify-center gap-10 pt-2">
                  {/* Share */}
                  <div className="flex flex-col items-center gap-3">
                    <motion.button
                      onClick={handleShare}
                      whileHover={{ scale: 1.05, y: -2 }}
                      whileTap={{ scale: 0.95 }}
                      transition={{
                        duration: 0.4,
                        ease: [0.25, 0.1, 0.25, 1],
                      }}
                      className="w-14 h-14 rounded-full gradient-button organic-shadow flex items-center justify-center"
                      aria-label={t('checkoutSuccess.share')}
                      type="button"
                    >
                      <svg
                        width="20"
                        height="20"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="white"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8" />
                        <polyline points="16 6 12 2 8 6" />
                        <line x1="12" y1="2" x2="12" y2="15" />
                      </svg>
                    </motion.button>
                    <span className="text-xs text-[#2D2A35] font-medium tracking-wide">
                      {t('checkoutSuccess.share')}
                    </span>
                  </div>

                  {/* Copy */}
                  <div className="flex flex-col items-center gap-3">
                    <motion.button
                      onClick={handleCopy}
                      whileHover={{ scale: 1.05, y: -2 }}
                      whileTap={{ scale: 0.95 }}
                      transition={{
                        duration: 0.4,
                        ease: [0.25, 0.1, 0.25, 1],
                      }}
                      className="w-14 h-14 rounded-full bg-white border border-[#E8E4EE] shadow-sm flex items-center justify-center"
                      aria-label={t('checkoutSuccess.copyLink')}
                      type="button"
                    >
                      <svg
                        width="18"
                        height="18"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="#9B87C7"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
                        <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
                      </svg>
                    </motion.button>

                    <motion.span
                      key={copied ? 'copied' : 'copy'}
                      initial={{ opacity: 0, y: 4 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.3 }}
                      className="text-xs text-[#9B95A3] font-medium tracking-wide"
                    >
                      {copied ? t('checkoutSuccess.copied') : t('checkoutSuccess.copyLink')}
                    </motion.span>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Back to home */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{
              delay: 1.2,
              duration: 1.2,
              ease: [0.25, 0.1, 0.25, 1],
            }}
            className="w-full max-w-xs pt-4"
          >
            <a
              href="https://www.niyyahmatch.com"
              target="_self"
              rel="noopener noreferrer"
            >
              <Button fullWidth variant="secondary" className="text-lg py-6">
                {t('checkoutSuccess.backHome')}
              </Button>
            </a>
          </motion.div>

          {/* Footer Note */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{
              delay: 1.2,
              duration: 1.2,
              ease: [0.25, 0.1, 0.25, 1],
            }}
            className="pt-8 space-y-2"
          >
            <p className="text-sm text-[#B5A8C3] font-light tracking-wide">
              {t('checkoutSuccess.footer')}
            </p>

            {sessionId && (
              <p className="text-xs text-[#D4CDD9] font-light break-all">
                {t('checkoutSuccess.sessionLabel')}: {sessionId}
              </p>
            )}
          </motion.div>
        </div>
      </div>
    </div>
  )
}